// "Why it came to you" as something a person can argue with (kanban 143454d3).
//
// Jess, 2026-10-06: the Show all / Show less toggle on the routing reason
// revealed nothing — the expanded state was the same sentence, taller. So
// the folded line and the expanded list are DIFFERENT content, built here
// from the backend's routing_rationale (services/signal_threads/
// routing_rationale.py), with the bare routing_reason as the fallback for
// an older backend or an item nothing routed.

import type { DoorThreadRead, RoutingRule } from '../../services/qcpInvestigations';

/** The one line the folded view shows: the rule that decided the route. */
export function whyLine(thread: Pick<DoorThreadRead, 'routing_reason' | 'routing_rationale'>): string {
  const summary = thread.routing_rationale?.summary?.trim();
  if (summary) return summary;
  return (thread.routing_reason ?? '').trim();
}

/** The rules in the order Merlin applies them — the expanded view. Empty
 *  when the backend sent none (then there is nothing more to show and the
 *  toggle stays hidden). */
export function whyRules(thread: Pick<DoorThreadRead, 'routing_rationale'>): RoutingRule[] {
  return thread.routing_rationale?.rules ?? [];
}

/** Whether expanding would show MORE than the folded line: at least one rule
 *  beyond the one the summary already says. */
export function hasWhyDetails(thread: Pick<DoorThreadRead, 'routing_reason' | 'routing_rationale'>): boolean {
  const rules = whyRules(thread);
  if (rules.length === 0) return false;
  const line = whyLine(thread);
  return rules.some((r) => r.text !== line) || rules.length > 1;
}

/** "Applied" / "Did not apply" — the chip beside each rule. */
export function ruleStateLabel(rule: Pick<RoutingRule, 'applied' | 'kind'>): string {
  if (rule.kind === 'classification') return "Merlin's read";
  return rule.applied ? 'Applied' : 'Did not apply';
}

/** The count line under the list: how many standing rules exist. */
export function rulesOnFileLine(thread: Pick<DoorThreadRead, 'routing_rationale'>): string | null {
  const n = thread.routing_rationale?.instructions_on_file;
  if (n === undefined || n === null) return null;
  if (n === 0) return 'No standing rules recorded yet. Send to someone else or Not an issue can record one.';
  return `${n} standing rule${n === 1 ? '' : 's'} on file.`;
}
